import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'

import { ServiceEmergencyMessage } from '@components/miscellaneous/Warnings'
import getArrivalWindow from '@utils/getArrivalWindow'
import urlPathSetter from '@utils/urlPathSetter'
import isEversourceGasLead from '@utils/isEversourceGasLead'

const Success = ({ currentStepIndex, setValidationSchema, values }) => {
  values.step = 'thankYou'
  urlPathSetter(values)
  const isService =
    values.path === 'hvac' && values.salesOrService === 'service'
  const isCap = values.path === 'hea' && values.paymentAssistance === 'yes'
  // Paused-gas HEA lead — TEMPORARY (SOF-1341). Submitted without an appointment.
  const isLead = isEversourceGasLead(values)
  // Every cohort that reaches this page WITHOUT a booked slot must be excluded here,
  // or values.schedule.split below throws after a successful submission.
  const hasAppointment = !isService && !isCap && !isLead

  useEffect(() => {
    setValidationSchema(null)
  }, [currentStepIndex])

  let appointmentTime, arrivalStartTime
  if (hasAppointment) {
    let [arrivalStart] = values.schedule.split(',')
    arrivalStart = moment(arrivalStart)
    arrivalStartTime = arrivalStart
    appointmentTime = getArrivalWindow(arrivalStart)
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6">
      {/* Top section: centered animation + heading */}
      <div
        className="flex flex-col items-center text-center pt-8 pb-6"
        style={{ animation: 'fadeUp 0.6s cubic-bezier(0.22,1,0.36,1) both' }}
      >

        {/* Animated checkmark with confetti */}
        <div className="relative mb-6">
          <div
            className="conf-a absolute -left-8 -top-4 w-3 h-3 rounded-sm"
            style={{ background: '#99c543', opacity: 0.9 }}
          />
          <div
            className="conf-b absolute -right-8 -top-2 w-2 h-4 rounded-sm"
            style={{ background: '#f2733b', opacity: 0.9 }}
          />
          <div
            className="conf-c absolute -left-4 top-4 w-4 h-2 rounded-sm"
            style={{ background: '#4675ad', opacity: 0.9 }}
          />
          <div
            className="conf-d absolute -right-4 top-6 w-2 h-3 rounded-sm"
            style={{ background: '#f2733b', opacity: 0.9 }}
          />
          <svg width="100" height="100" viewBox="0 0 100 100" fill="none">
            <circle
              className="check-circle"
              cx="50"
              cy="50"
              r="46"
              fill="#f0f8e0"
              stroke="#99c543"
              strokeWidth="4"
            />
            <path
              className="check-path"
              d="M30 50l14 14 26-26"
              stroke="#99c543"
              strokeWidth="5"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        </div>

        {/* Heading */}
        <h1
          className="text-4xl md:text-5xl text-stone-900 mb-4"
          style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontWeight: 400 }}
        >
          {isLead
            ? "Thanks — we've received your information!"
            : "You're all set!"}
        </h1>

        {/* Subtitle */}
        {hasAppointment && (
          <p className="text-stone-600 text-xl font-medium mb-2 max-w-lg">
            Your appointment has been confirmed.
          </p>
        )}
        {isCap && (
          <p className="text-stone-600 text-xl font-medium mb-2 max-w-lg">
            Your information has been received.
          </p>
        )}
        {isService && (
          <p className="text-stone-600 text-xl font-medium mb-2 max-w-lg">
            Your service request has been received.
          </p>
        )}
        {isLead && (
          <p className="text-stone-600 text-xl font-medium mb-2 max-w-2xl">
            Due to temporary Mass Save program availability, HomeWorks Energy is not
            currently scheduling new Home Energy Assessments for your utility through the
            end of November.
          </p>
        )}
      </div>

      {/* Content grid: two columns on md+, single column on mobile.
          Leads have no left-column content, so they get a single centered column. */}
      <div className={`grid grid-cols-1 ${isLead ? '' : 'md:grid-cols-2'} gap-6 pb-10`}>

        {/* Left column: appointment/service card + specialist note */}
        {!isLead && (
        <div>
          {/* Appointment card — HEA / HVAC Sales / HealthyHome */}
          {hasAppointment && (
            <div
              className="bg-white rounded-2xl p-6 mb-5"
              style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06),0 4px 16px rgba(0,0,0,0.06)' }}
            >
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: '#f0f8e0' }}
                >
                  <svg width="22" height="22" viewBox="0 0 20 20" fill="none">
                    <rect x="3" y="2" width="14" height="16" rx="2" stroke="#99c543" strokeWidth="1.5" />
                    <path d="M7 7h6M7 10h6M7 13h4" stroke="#99c543" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </div>
                <div>
                  <div className="text-sm text-stone-400 font-semibold uppercase tracking-widest">
                    Appointment
                  </div>
                  <div className="font-semibold text-stone-900 text-base mt-0.5">
                    {arrivalStartTime?.format('dddd, MMMM Do')} · {appointmentTime}
                  </div>
                </div>
              </div>
              <div className="h-px bg-stone-100 my-3" />
              <div className="text-sm text-stone-400 text-center">
                Confirmation sent to your email
              </div>
            </div>
          )}

          {/* Service request card */}
          {isService && (
            <div
              className="bg-white rounded-2xl p-6 mb-5"
              style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06),0 4px 16px rgba(0,0,0,0.06)' }}
            >
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: '#fef3ed' }}
                >
                  <svg width="22" height="22" viewBox="0 0 20 20" fill="none">
                    <path d="M14 5l-2 2-5 5-1.5 3.5 3.5-1.5 5-5 2-2-2-2z" fill="#f2733b" opacity="0.85" />
                    <path d="M12 7l2 2" stroke="white" strokeWidth="1.2" strokeLinecap="round" />
                  </svg>
                </div>
                <div>
                  <div className="text-sm text-stone-400 font-semibold uppercase tracking-widest">
                    Service Request
                  </div>
                  <div className="font-semibold text-stone-900 text-base mt-0.5">
                    Our team will contact you within 24 hours
                  </div>
                </div>
              </div>
              <div className="h-px bg-stone-100 my-3" />
              <div className="text-sm text-stone-400 text-center">
                Managed via ServiceTitan scheduling system
              </div>
              {values.serviceReason === 'fix' && (
                <div className="mt-3">
                  <ServiceEmergencyMessage />
                </div>
              )}
            </div>
          )}

          {/* Specialist contact note */}
          {hasAppointment && (
            <p className="text-stone-500 text-base leading-relaxed">
              One of our{' '}
              {values.path === 'hvac' ? 'Account Managers' : 'Intake Specialists'}{' '}
              will contact you shortly to confirm your appointment, ask some additional questions, and gather utility information.
            </p>
          )}
        </div>
        )}

        {/* Right column: path-specific body copy */}
        <div
          className={`text-stone-600 text-base${
            isLead ? ' max-w-2xl mx-auto w-full' : ''
          }`}
        >
          {values.path === 'hea' && !isCap && !isLead && (
            <div
              className="bg-white rounded-2xl p-6"
              style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06),0 4px 16px rgba(0,0,0,0.06)' }}
            >
              <p className="mb-4 font-medium text-stone-700">
                Your Home Energy Assessment will take 2–3 hours. Please prepare the following for the Home Energy Specialist:
              </p>
              <ul className="mb-4 space-y-2">
                <li className="flex items-start gap-3">
                  <svg width="16" height="16" viewBox="0 0 14 14" fill="none" className="mt-0.5 flex-shrink-0">
                    <circle cx="7" cy="7" r="6" fill="#f0f8e0" stroke="#99c543" strokeWidth="1.2" />
                    <path d="M4 7l2 2 4-4" stroke="#99c543" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  A recent copy of your utility bills
                </li>
                <li className="flex items-start gap-3">
                  <svg width="16" height="16" viewBox="0 0 14 14" fill="none" className="mt-0.5 flex-shrink-0">
                    <circle cx="7" cy="7" r="6" fill="#f0f8e0" stroke="#99c543" strokeWidth="1.2" />
                    <path d="M4 7l2 2 4-4" stroke="#99c543" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Clear access for basement, attic, and crawl spaces
                </li>
              </ul>
              <p className="mb-3">
                2–3 days prior to the visit, one of our customer support representatives will contact you to confirm your appointment via the phone number you provided.
              </p>
              <p>
                If we are unable to get in touch after 3 attempts, the appointment will be canceled in order to free up the time slot for other Mass Save customers.
              </p>
            </div>
          )}

          {isCap && (
            <div
              className="bg-white rounded-2xl p-6"
              style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06),0 4px 16px rgba(0,0,0,0.06)' }}
            >
              <p className="mb-3">
                Thank you! We&apos;ve received your information.
              </p>
              <p className="mb-4">
                Because you&apos;re applying to the Mass Save® Income-Eligible program, we&apos;ll submit the details you provided to the Statewide Client Services Center (SCSC) for eligibility review. Scheduling can only happen after approval.
              </p>
              <p className="font-semibold text-stone-700 mb-3">What happens next?</p>
              <ol className="space-y-3 mb-4">
                <li>
                  <strong>1. Submission to SCSC:</strong> Our team will send your application to the Statewide Client Services Center, which manages applications for all Community Action Programs (CAP) energy efficiency work.
                </li>
                <li>
                  <strong>2. SCSC/CAP review:</strong> Timelines vary, and SCSC or your local CAP may contact you for additional information. Please respond promptly to avoid delays.
                </li>
                <li>
                  <strong>3. If approved:</strong> You&apos;ll be eligible for a no-cost Mass Save® Home Energy Assessment and possibly follow-on energy efficiency work. You&apos;ll be contacted right away to schedule.
                </li>
                <li>
                  <strong>4. In addition:</strong> You can contact the SCSC with further questions at 833-888-1326.
                </li>
              </ol>
              <p>Thank you again, we look forward to speaking with you soon!<br />— The HomeWorks Energy Team</p>
            </div>
          )}

          {/* Paused-gas lead — TEMPORARY (SOF-1341) */}
          {isLead && (
            <div
              className="bg-white rounded-2xl p-6"
              style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06),0 4px 16px rgba(0,0,0,0.06)' }}
            >
              <p className="mb-4">
                You don&apos;t need to submit your information again. We&apos;ve saved your
                request and will contact you when HomeWorks Energy is able to resume
                scheduling.
              </p>
              <p>
                If you would prefer to explore an assessment with another participating
                provider sooner, you may contact Mass Save at{' '}
                <a
                  href="tel:18665277283"
                  className="font-semibold text-stone-700 underline"
                >
                  1-866-527-SAVE (7283)
                </a>
                .
              </p>
            </div>
          )}

          {values.path === 'hvac' && !isService && (
            <div
              className="bg-white rounded-2xl p-6"
              style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06),0 4px 16px rgba(0,0,0,0.06)' }}
            >
              <p className="mb-3">
                Your appointment will take approximately 2 hours.
              </p>
              <p className="mb-3">
                You will receive an email confirmation shortly and can expect one of our customer support representatives to contact you to confirm your appointment and gather any additional details necessary via the phone number you provided.
              </p>
              <p>
                If we are unable to get in contact with you by the day before the assessment, we will unfortunately need to cancel your appointment to allow other customers the opportunity to schedule.
              </p>
            </div>
          )}

          {values.path === 'hvac' && isService && (
            <div
              className="bg-white rounded-2xl p-6"
              style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06),0 4px 16px rgba(0,0,0,0.06)' }}
            >
              <p className="mb-3">
                One of our service technicians will reach out within 24 hours to discuss your request and schedule a visit.
              </p>
              <p>
                If you have an urgent issue, please call us directly at <strong>(781) 305-3319</strong>.
              </p>
            </div>
          )}

          {values.path === 'healthyhome' && (
            <div
              className="bg-white rounded-2xl p-6"
              style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06),0 4px 16px rgba(0,0,0,0.06)' }}
            >
              <p className="mb-3">
                Your appointment will take up to 2 hours.
              </p>
              <p className="mb-3">
                You will receive an email confirmation shortly and can expect one of our customer support representatives to contact you to confirm your appointment via the phone number you provided.
              </p>
              <p>
                If we are unable to get in contact with you by the day before the appointment, we will unfortunately need to cancel your appointment to allow other customers the opportunity to schedule.
              </p>
            </div>
          )}
        </div>

      </div>
    </div>
  )
}

Success.info = {
  title: 'Success',
  key: 'success',
}

Success.propTypes = {
  currentStepIndex: PropTypes.number.isRequired,
  setValidationSchema: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
}

export default Success
